import { useEffect, useRef, useState, type FormEvent } from "react";
import { Building2, CheckCircle2, KeyRound, LoaderCircle, MailCheck } from "lucide-react";
import { Link, useSearchParams } from "../router.js";
import { api } from "../api.js";
import { consoleText, localizedError } from "../console-i18n.js";
import { useLocale } from "../i18n.js";

function AuthFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="login-page">
      <section className="login-panel auth-flow-panel">
        <div className="login-brand">
          <span className="brand-mark">
            <KeyRound size={21} />
          </span>
          <strong>Lobster Verify</strong>
        </div>
        {children}
      </section>
    </main>
  );
}

interface RegistrationPolicy {
  enabled: boolean;
  termsUrl: string | null;
  privacyUrl: string | null;
  freePlan: {
    organizationLimit: number;
    siteLimit: number;
    memberLimit: number;
    monthlyVerificationLimit: number;
  };
}

export function RegisterPage() {
  const { locale } = useLocale();
  const copy = consoleText(locale).auth;
  const [policy, setPolicy] = useState<RegistrationPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    void api<{ registration: RegistrationPolicy }>("/api/auth/login-methods")
      .then((result) => setPolicy(result.registration))
      .catch((caught: unknown) =>
        setError(localizedError(locale, caught, copy.registrationUnavailable)),
      )
      .finally(() => setLoading(false));
  }, [copy.registrationUnavailable, locale]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({
          email: form.get("email"),
          displayName: form.get("displayName"),
          organizationName: form.get("organizationName"),
          password: form.get("password"),
          passwordConfirmation: form.get("passwordConfirmation"),
          termsAccepted: form.get("termsAccepted") === "on",
          website: form.get("website"),
        }),
      });
      setSent(true);
    } catch (caught) {
      setError(localizedError(locale, caught, copy.registrationFailed));
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <AuthFrame>
      <h1>{copy.createAccount}</h1>
      {loading ? (
        <div className="auth-result">
          <LoaderCircle className="spin" />
        </div>
      ) : sent ? (
        <div className="auth-result" role="status">
          <MailCheck />
          <p>{copy.checkInbox}</p>
          <Link className="secondary-button" to="/login">
            {copy.backToSignIn}
          </Link>
        </div>
      ) : policy?.enabled ? (
        <>
          <p>{copy.freeStart}</p>
          <div className="registration-plan" aria-label={copy.freePlanLimits}>
            <Building2 />
            <span>
              {policy.freePlan.siteLimit} {copy.sites} · {policy.freePlan.memberLimit}{" "}
              {copy.members} · {policy.freePlan.monthlyVerificationLimit.toLocaleString(locale)}{" "}
              {copy.verificationsPerMonth}
            </span>
          </div>
          <form onSubmit={(event) => void submit(event)}>
            <label>
              {copy.yourName}
              <input
                name="displayName"
                autoComplete="name"
                minLength={2}
                maxLength={120}
                required
              />
            </label>
            <label>
              {copy.workEmail}
              <input name="email" type="email" autoComplete="email" maxLength={320} required />
            </label>
            <label>
              {copy.organizationName}
              <input
                name="organizationName"
                autoComplete="organization"
                minLength={2}
                maxLength={160}
                required
              />
            </label>
            <label>
              {copy.password}
              <input
                name="password"
                type="password"
                autoComplete="new-password"
                minLength={14}
                required
              />
            </label>
            <label>
              {copy.confirmPassword}
              <input
                name="passwordConfirmation"
                type="password"
                autoComplete="new-password"
                minLength={14}
                required
              />
            </label>
            <label className="registration-honeypot" aria-hidden="true">
              {copy.website}
              <input name="website" tabIndex={-1} autoComplete="off" />
            </label>
            <label className="terms-check">
              <input name="termsAccepted" type="checkbox" required />
              <span>
                {copy.agreePrefix}{" "}
                {policy.termsUrl ? (
                  <a href={policy.termsUrl} target="_blank" rel="noreferrer">
                    {copy.terms}
                  </a>
                ) : (
                  copy.terms
                )}
                {` ${copy.and} `}
                {policy.privacyUrl ? (
                  <a href={policy.privacyUrl} target="_blank" rel="noreferrer">
                    {copy.privacy}
                  </a>
                ) : (
                  copy.privacy
                )}
                .
              </span>
            </label>
            {error ? <p className="form-error">{error}</p> : null}
            <button className="primary-button login-submit" disabled={submitting}>
              {submitting ? <LoaderCircle className="spin" /> : null}
              {copy.createAccount}
            </button>
          </form>
          <div className="auth-links">
            <Link to="/login">{copy.alreadyAccount}</Link>
          </div>
        </>
      ) : (
        <div className="auth-result error-state">
          <p>{error || copy.publicRegistrationUnavailable}</p>
          <Link className="secondary-button" to="/login">
            {copy.backToSignIn}
          </Link>
        </div>
      )}
    </AuthFrame>
  );
}

export function VerifyEmailPage() {
  const { locale } = useLocale();
  const copy = consoleText(locale).auth;
  const [search] = useSearchParams();
  const token = search.get("token") ?? "";
  const requested = useRef(false);
  const [status, setStatus] = useState<"loading" | "complete" | "error">(
    token ? "loading" : "error",
  );
  const [error, setError] = useState(token ? "" : copy.verificationLinkIncomplete);
  useEffect(() => {
    if (!token || requested.current) return;
    requested.current = true;
    void api("/api/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    })
      .then(() => setStatus("complete"))
      .catch((caught: unknown) => {
        setError(localizedError(locale, caught, copy.emailVerificationFailed));
        setStatus("error");
      });
  }, [copy.emailVerificationFailed, locale, token]);
  return (
    <AuthFrame>
      <h1>{copy.verifyEmail}</h1>
      {status === "loading" ? (
        <div className="auth-result" role="status">
          <LoaderCircle className="spin" />
          <p>{copy.creatingAccount}</p>
        </div>
      ) : status === "complete" ? (
        <div className="auth-result" role="status">
          <CheckCircle2 />
          <p>{copy.accountReady}</p>
          <Link className="primary-button" to="/login">
            {copy.signIn}
          </Link>
        </div>
      ) : (
        <div className="auth-result error-state" role="alert">
          <p>{error}</p>
          <Link className="secondary-button" to="/register">
            {copy.registerAgain}
          </Link>
        </div>
      )}
    </AuthFrame>
  );
}

export function ForgotPasswordPage() {
  const { locale } = useLocale();
  const copy = consoleText(locale).auth;
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: form.get("email") }),
      });
      setSent(true);
    } catch (caught) {
      setError(localizedError(locale, caught, copy.requestFailed));
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <AuthFrame>
      <h1>{copy.resetPassword}</h1>
      {sent ? (
        <div className="auth-result" role="status">
          <MailCheck />
          <p>{copy.resetMailSent}</p>
          <Link className="secondary-button" to="/login">
            {copy.backToSignIn}
          </Link>
        </div>
      ) : (
        <>
          <p>{copy.enterEmail}</p>
          <form onSubmit={(event) => void submit(event)}>
            <label>
              {consoleText(locale).common.email}
              <input name="email" type="email" autoComplete="email" required />
            </label>
            {error ? <p className="form-error">{error}</p> : null}
            <button className="primary-button login-submit" disabled={submitting}>
              {submitting ? <LoaderCircle className="spin" /> : null}
              {copy.sendResetLink}
            </button>
          </form>
          <div className="auth-links">
            <Link to="/login">{copy.backToSignIn}</Link>
          </div>
        </>
      )}
    </AuthFrame>
  );
}

export function ResetPasswordPage() {
  const { locale } = useLocale();
  const copy = consoleText(locale).auth;
  const [search] = useSearchParams();
  const token = search.get("token") ?? "";
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    setError("");
    try {
      await api("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({
          token,
          password: form.get("password"),
          passwordConfirmation: form.get("passwordConfirmation"),
        }),
      });
      setComplete(true);
    } catch (caught) {
      setError(localizedError(locale, caught, copy.passwordResetFailed));
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <AuthFrame>
      <h1>{copy.choosePassword}</h1>
      {!token ? (
        <div className="auth-result error-state">
          <p>{copy.resetLinkIncomplete}</p>
          <Link className="secondary-button" to="/forgot-password">
            {copy.requestAnotherLink}
          </Link>
        </div>
      ) : complete ? (
        <div className="auth-result" role="status">
          <CheckCircle2 />
          <p>{copy.passwordChanged}</p>
          <Link className="primary-button" to="/login">
            {copy.signIn}
          </Link>
        </div>
      ) : (
        <form onSubmit={(event) => void submit(event)}>
          <label>
            {copy.newPassword}
            <input
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={14}
              required
            />
          </label>
          <label>
            {copy.confirmNewPassword}
            <input
              name="passwordConfirmation"
              type="password"
              autoComplete="new-password"
              minLength={14}
              required
            />
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <button className="primary-button login-submit" disabled={submitting}>
            {submitting ? <LoaderCircle className="spin" /> : null}
            {copy.changePassword}
          </button>
        </form>
      )}
    </AuthFrame>
  );
}

interface InvitationDetails {
  email: string;
  organizationName: string;
  expiresAt: string;
  accountExists: boolean;
}

export function AcceptInvitationPage() {
  const { locale } = useLocale();
  const copy = consoleText(locale).auth;
  const [search] = useSearchParams();
  const token = search.get("token") ?? "";
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(token ? "" : copy.invitationLinkIncomplete);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (!token) return;
    void api<InvitationDetails>("/api/invitations/details", {
      method: "POST",
      body: JSON.stringify({ token }),
    })
      .then(setDetails)
      .catch((caught: unknown) =>
        setError(localizedError(locale, caught, copy.invitationUnavailable)),
      )
      .finally(() => setLoading(false));
  }, [copy.invitationUnavailable, locale, token]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    setError("");
    try {
      await api("/api/invitations/accept", {
        method: "POST",
        body: JSON.stringify({
          token,
          ...(!details?.accountExists
            ? {
                displayName: form.get("displayName"),
                password: form.get("password"),
                passwordConfirmation: form.get("passwordConfirmation"),
              }
            : {}),
        }),
      });
      setComplete(true);
    } catch (caught) {
      setError(localizedError(locale, caught, copy.invitationAcceptFailed));
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <AuthFrame>
      <h1>{copy.organizationInvitation}</h1>
      {loading ? (
        <div className="auth-result">
          <LoaderCircle className="spin" />
        </div>
      ) : complete ? (
        <div className="auth-result" role="status">
          <CheckCircle2 />
          <p>{copy.invitationAccepted}</p>
          <Link className="primary-button" to="/login">
            {copy.signIn}
          </Link>
        </div>
      ) : details ? (
        <>
          <p>
            <strong>{details.organizationName}</strong> {copy.invited}{" "}
            <strong>{details.email}</strong>
            {locale === "zh-CN" ? "。" : "."}
          </p>
          <form onSubmit={(event) => void submit(event)}>
            {!details.accountExists ? (
              <>
                <label>
                  {copy.displayName}
                  <input name="displayName" autoComplete="name" minLength={2} required />
                </label>
                <label>
                  {copy.password}
                  <input
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    minLength={14}
                    required
                  />
                </label>
                <label>
                  {copy.confirmPassword}
                  <input
                    name="passwordConfirmation"
                    type="password"
                    autoComplete="new-password"
                    minLength={14}
                    required
                  />
                </label>
              </>
            ) : null}
            {error ? <p className="form-error">{error}</p> : null}
            <button className="primary-button login-submit" disabled={submitting}>
              {submitting ? <LoaderCircle className="spin" /> : null}
              {copy.acceptInvitation}
            </button>
          </form>
        </>
      ) : (
        <div className="auth-result error-state">
          <p>{error}</p>
          <Link className="secondary-button" to="/login">
            {copy.backToSignIn}
          </Link>
        </div>
      )}
    </AuthFrame>
  );
}
